import { useEffect } from 'react'
import { F0AuraVoiceAnimation, F0Button } from '@factorialco/f0-react'
import {
  RoomAudioRenderer,
  SessionProvider,
  useRoomContext,
  useSession,
  useVoiceAssistant,
} from '@livekit/components-react'
import { ConnectionState } from 'livekit-client'

import i18n from 'modules/core/lib/i18n'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { getCursor, removeCursor } from 'modules/ai_voice/lib/cursor'
import { disposeAiMask, initAiMask, showAiMask } from 'modules/ai_voice/lib/mask'
import { registerRouteObserver, unregisterRouteObserver } from 'modules/ai_voice/lib/route-observer'
import { registerRpcMethods, unregisterRpcMethods } from 'modules/ai_voice/lib/rpc'
import { useVoiceSessionTokenSource } from 'modules/ai_voice/hooks/useVoiceSessionTokenSource'

function RoomContext() {
  const room = useRoomContext()

  useEffect(() => {
    initAiMask()
    showAiMask()
    getCursor()
    registerRpcMethods(room)
    registerRouteObserver(room)
    return () => {
      disposeAiMask()
      removeCursor()
      unregisterRpcMethods(room)
      unregisterRouteObserver()
    }
  }, [room])

  return null
}

function VoiceAnimationConnected() {
  const { audioTrack, state } = useVoiceAssistant()

  return <F0AuraVoiceAnimation state={state} audioTrack={audioTrack} />
}

interface VoiceClientProps {
  agent: string
  task: string
}

export function VoiceClient({ agent, task }: VoiceClientProps) {
  const { company, employee, canCreateEmployees } = useCurrentSession()
  const participantAttributes = {
    agent,
    task,
    url: window.location.href,
    locale: i18n.locale,
    firstName: employee.firstName,
    lastName: employee.lastName,
    userId: employee.id,
    isAdmin: canCreateEmployees,
    companyName: company.name,
    companyId: company.id,
  }
  const tokenSource = useVoiceSessionTokenSource(participantAttributes)
  const session = useSession(tokenSource)

  async function handleConnect() {
    await session.start()
  }

  async function handleDisconnect() {
    await session.end()
  }

  const isConnected = session.isConnected
  const isConnecting = session.connectionState === ConnectionState.Connecting

  return (
    <SessionProvider session={session}>
      {!isConnected && (
        <>
          <div className="factorial-voice-agent-container">
            <F0AuraVoiceAnimation state={isConnecting ? 'connecting' : 'disconnected'} />
          </div>
          <F0Button
            onClick={handleConnect}
            disabled={isConnecting}
            label={isConnecting ? 'Connecting...' : 'Start'}
            variant="default"
          />
        </>
      )}

      {isConnected && (
        <>
          <RoomContext />
          <RoomAudioRenderer />
          <div className="factorial-voice-agent-container">
            <VoiceAnimationConnected />
          </div>
          <F0Button onClick={handleDisconnect} label="Stop" variant="neutral" />
        </>
      )}
    </SessionProvider>
  )
}
